import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ServicesPicker } from "@/components/office/MultiUserImport";
import { ArrowLeft, Plus, Trash2, CheckCircle2, XCircle, Send, Info } from "lucide-react";

// Solo servizi associabili a una Sede operativa (stesso criterio del form singolo):
// kind 'operative_site' + eccezione casa_di_riposo (ibrido: sede per il ruolo ASA / OSS).
const isSiteService = (s) => s.entity_kind === "operative_site" || s.slug === "casa_di_riposo";

const parseHours = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

// Colonne dinamiche: comuni sempre presenti + specifiche di rendicontazione per servizio.
// I campi di rendicontazione sono facoltativi (come nel form singolo).
function deriveColumns(services) {
  const has = (s) => services.includes(s);
  const cols = [
    { key: "denominazione", label: "Denominazione della sede", kind: "text", required: true },
    { key: "indirizzo", label: "Indirizzo", kind: "text", required: false },
    { key: "comune", label: "Comune", kind: "text", required: false },
    { key: "note", label: "Note", kind: "text", required: false },
  ];
  if (has("pre_scuola")) {
    cols.push({ key: "pre_educatore", label: "PRE · Educatore (h/gg)", kind: "number", required: false, rp: ["pre_scuola", "educatore"] });
    cols.push({ key: "pre_sorveglianza", label: "PRE · Sorveglianza (h/gg)", kind: "number", required: false, rp: ["pre_scuola", "sorveglianza"] });
  }
  if (has("post_scuola")) {
    cols.push({ key: "post_educatore", label: "Post · Educatore (h/gg)", kind: "number", required: false, rp: ["post_scuola", "educatore"] });
    cols.push({ key: "post_sorveglianza", label: "Post · Sorveglianza (h/gg)", kind: "number", required: false, rp: ["post_scuola", "sorveglianza"] });
  }
  if (has("spazio_compiti")) {
    cols.push({ key: "sc_monte_ore", label: "Spazio compiti · Monte ore sett.", kind: "number", required: false, rp: ["spazio_compiti", "monte_ore_settimanale"] });
  }
  if (has("assistenza_scuolabus")) {
    cols.push({ key: "scuolabus_monte_ore", label: "Assistenza Scuolabus · Monte ore sett.", kind: "number", required: false, rp: ["assistenza_scuolabus", "monte_ore_settimanale"] });
  }
  if (has("assistenza_mensa")) {
    cols.push({ key: "mensa_monte_ore", label: "Assistenza Mensa · Monte ore sett.", kind: "number", required: false, rp: ["assistenza_mensa", "monte_ore_settimanale"] });
  }
  if (has("centro_aggregativo")) {
    cols.push({ key: "centroagg_monte_ore", label: "Centro aggregativo · Monte ore sett.", kind: "number", required: false, rp: ["centro_aggregativo", "monte_ore_settimanale"] });
  }
  return cols;
}

const emptyRow = () => ({ status: "", error: "" });

export function MultiSiteImport({ onClose }) {
  const [appalti, setAppalti] = useState([]);
  const [appaltoId, setAppaltoId] = useState("");
  const [services, setServices] = useState([]);
  const [rows, setRows] = useState(() => Array.from({ length: 5 }, emptyRow));
  const [addN, setAddN] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    api.get("/my-appalti").then((r) => {
      setAppalti(r.data);
      if (r.data.length === 1) setAppaltoId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const appalto = appalti.find((a) => a.id === appaltoId);
  const serviceOptions = useMemo(
    () => (appalto?.servizi || []).filter((s) => s.stato === "attivo" && isSiteService(s)),
    [appalto]
  );
  const columns = useMemo(() => deriveColumns(services), [services]);
  const configured = !!appaltoId && services.length > 0;

  const rowHasData = (r) => columns.some((c) => {
    const v = r[c.key];
    return v !== "" && v !== undefined && v !== null;
  });
  const isEmptyRow = (r) => !rowHasData(r);
  const tableHasData = rows.some((r) => r.status !== "created" && rowHasData(r));

  const rowErrors = (r) => {
    if (r.status === "created") return {};
    if (isEmptyRow(r)) return {};
    const errs = {};
    columns.forEach((c) => {
      const val = (r[c.key] ?? "").toString().trim();
      if (c.kind === "number") {
        if (val !== "" && parseHours(val) === null) errs[c.key] = "Valore non valido";
        else if (val !== "" && parseHours(val) < 0) errs[c.key] = "Non può essere negativo";
        return;
      }
      if (c.required !== false && !val) errs[c.key] = "Obbligatorio";
    });
    return errs;
  };
  const errorsByRow = rows.map(rowErrors);
  const entered = rows.filter((r) => !isEmptyRow(r)).length;
  const createdCount = rows.filter((r) => r.status === "created").length;
  const readyIdx = rows.map((r, i) => ({ r, i, e: errorsByRow[i] }))
    .filter(({ r, e }) => !isEmptyRow(r) && r.status !== "created" && Object.keys(e).length === 0)
    .map(({ i }) => i);
  const ready = readyIdx.length;
  const toFix = rows.filter((r, i) => !isEmptyRow(r) && r.status !== "created" && Object.keys(errorsByRow[i]).length > 0).length;

  const touch = (row) => ({ ...row, status: row.status === "created" ? row.status : "", error: "" });
  const setCell = (idx, key, value) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...touch(row), [key]: value } : row)));
    setResult(null);
  };
  const addRows = (n) => {
    const count = Math.max(1, Math.min(500, Math.floor(Number(n) || 0)));
    setRows((prev) => [...prev, ...Array.from({ length: count }, emptyRow)]);
    setResult(null);
  };
  const deleteRow = (idx) => {
    setRows((prev) => (prev.length <= 1 ? [emptyRow()] : prev.filter((_, i) => i !== idx)));
    setResult(null);
  };

  const pasteCols = columns; // tutte le colonne sono testo/numero → incollabili
  const handlePaste = (e, rowIdx, pasteIdx) => {
    const text = e.clipboardData?.getData("text");
    if (!text) return;
    e.preventDefault();
    let lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    while (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
    const parsed = lines.map((l) => l.split("\t"));
    setRows((prev) => {
      const next = prev.map((row) => ({ ...row }));
      const needed = rowIdx + parsed.length;
      while (next.length < needed) next.push(emptyRow());
      parsed.forEach((cells, ri) => {
        cells.forEach((val, ci) => {
          const col = pasteCols[pasteIdx + ci];
          if (!col) return;
          if (next[rowIdx + ri].status === "created") return;
          next[rowIdx + ri][col.key] = (val ?? "").trim();
          next[rowIdx + ri].status = ""; next[rowIdx + ri].error = "";
        });
      });
      return next;
    });
    setResult(null);
  };

  const changeAppalto = (newId) => {
    if (newId === appaltoId) return;
    const apply = () => {
      setAppaltoId(newId); setServices([]);
      setRows((prev) => prev.map((r) => (r.status === "created" ? r : {
        ...emptyRow(), denominazione: r.denominazione || "", indirizzo: r.indirizzo || "", comune: r.comune || "", note: r.note || "",
      })));
      setResult(null);
    };
    if (tableHasData || services.length > 0) {
      setConfirm({ title: "Cambiare appalto?", desc: "Cambiando appalto verranno ripristinati i servizi e i campi di rendicontazione collegati. I dati anagrafici della sede (denominazione, indirizzo, comune, note) restano.", apply });
    } else apply();
  };

  const changeServices = (next) => {
    const removed = services.filter((s) => !next.includes(s));
    const applied = () => {
      const keepKeys = new Set(deriveColumns(next).map((c) => c.key));
      setRows((prev) => prev.map((r) => {
        if (r.status === "created") return r;
        const nr = { ...r };
        columns.forEach((c) => { if (!keepKeys.has(c.key)) delete nr[c.key]; });
        return { ...touch(nr) };
      }));
      setServices(next); setResult(null);
    };
    if (removed.length > 0) {
      const keepKeys = new Set(deriveColumns(next).map((c) => c.key));
      const dropped = columns.filter((c) => !keepKeys.has(c.key));
      const dataLost = rows.some((r) => r.status !== "created" && dropped.some((c) => {
        const v = r[c.key]; return v !== "" && v !== undefined && v !== null;
      }));
      if (dataLost) {
        setConfirm({ title: "Rimuovere il servizio?", desc: "Alcune colonne di rendicontazione collegate al servizio rimosso contengono dati che verranno eliminati. Vuoi procedere?", apply: applied });
        return;
      }
    }
    applied();
  };

  const buildPayload = (r) => {
    const p = {
      appalto_id: appaltoId, service_types: services,
      denominazione: (r.denominazione || "").trim(),
      indirizzo: (r.indirizzo || "").trim(),
      comune: (r.comune || "").trim(),
      note: (r.note || "").trim(),
    };
    const rp = {};
    columns.forEach((c) => {
      if (!c.rp) return;
      const [svc, key] = c.rp;
      rp[svc] = rp[svc] || {};
      rp[svc][key] = r[c.key] ?? "";
    });
    p.rendic_params = rp;
    return p;
  };

  const submit = async () => {
    if (ready === 0 || submitting) return;
    setSubmitting(true);
    let created = 0, error = 0;
    const updates = {};
    for (const idx of readyIdx) {
      try {
        await api.post("/admin/operative-sites", buildPayload(rows[idx]));
        updates[idx] = { status: "created", error: "" }; created += 1;
      } catch (err) {
        const detail = formatApiError(err.response?.data?.detail) || err.message || "Errore";
        updates[idx] = { status: "error", error: detail }; error += 1;
      }
    }
    setRows((prev) => prev.map((row, i) => (updates[i] ? { ...row, ...updates[i] } : row)));
    setResult({ created, error });
    setSubmitting(false);
    if (created > 0) toast.success(`${created} ${created === 1 ? "sede creata" : "sedi create"}`);
    if (error > 0) toast.error(`${error} ${error === 1 ? "riga non creata" : "righe non create"}`);
  };

  const statusBadge = (r) => {
    if (r.status === "created") return <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="mo-row-created"><CheckCircle2 className="h-3.5 w-3.5" />Creata</span>;
    if (r.status === "error") return <span className="inline-flex items-center gap-1 text-xs font-semibold text-destructive" data-testid="mo-row-error"><XCircle className="h-3.5 w-3.5" />Errore</span>;
    return null;
  };

  const renderCell = (row, r, col) => {
    const errs = errorsByRow[r];
    const locked = row.status === "created";
    const err = errs[col.key];
    const cls = `w-full h-9 px-2 rounded-md border text-sm bg-white outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${err ? "border-destructive bg-destructive/5" : "border-slate-200"} ${locked ? "text-slate-400" : "text-slate-800"}`;
    const pasteIdx = pasteCols.findIndex((c) => c.key === col.key);
    return (
      <>
        <input data-testid={`mo-cell-${r}-${col.key}`} disabled={locked} value={row[col.key] ?? ""}
          onChange={(e) => setCell(r, col.key, e.target.value)} onPaste={(e) => handlePaste(e, r, pasteIdx)}
          className={cls} title={err || ""} />
        {err && <p className="text-[11px] text-destructive mt-0.5 px-1">{err}</p>}
      </>
    );
  };

  return (
    <div data-testid="multi-site-import">
      <button data-testid="mo-back-btn" onClick={onClose}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline mb-4">
        <ArrowLeft className="h-4 w-4" /> Torna alle Sedi operative
      </button>

      <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">Inserimento multiplo sedi operative</h2>
      <p className="text-sm text-slate-500 mb-4">Definisci Appalto e Servizi, poi compila la tabella o incolla da Excel / Google Sheets. Ogni riga valida crea una sede identica a "Aggiungi".</p>

      <div className="mb-4 rounded-xl border border-slate-200 bg-white shadow-sm p-4 grid gap-4 sm:grid-cols-2" data-testid="mo-context">
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-slate-600 block">Appalto <span className="text-destructive">*</span></label>
          <Select value={appaltoId} onValueChange={changeAppalto} disabled={appalti.length <= 1}>
            <SelectTrigger data-testid="mo-appalto" className="h-11"><SelectValue placeholder="Seleziona l'appalto…" /></SelectTrigger>
            <SelectContent>{appalti.map((a) => (<SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>))}</SelectContent>
          </Select>
          {appalti.length === 0 && <p className="text-xs text-amber-600">Nessun appalto attivo nel tuo perimetro.</p>}
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-slate-600 block">Servizi <span className="text-destructive">*</span></label>
          <ServicesPicker value={services} onChange={changeServices} options={serviceOptions} disabled={!appaltoId} testid="mo-services" />
          {appaltoId && serviceOptions.length === 0 && <p className="text-xs text-amber-600">Nessun servizio sede abilitato in questo appalto.</p>}
        </div>
      </div>

      {!configured ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500" data-testid="mo-need-context">
          Seleziona un appalto e almeno un servizio per generare la tabella.
        </div>
      ) : (
        <>
          <div className="mb-4 rounded-lg bg-[#EDEAF6] border border-[#D6D0EA] text-slate-700 text-sm px-3 py-2 flex items-start gap-2" data-testid="mo-paste-hint">
            <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
            <span>Incolla (Ctrl/Cmd+V) più righe direttamente nella tabella. I parametri di rendicontazione sono facoltativi.</span>
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-4 text-sm" data-testid="mo-summary">
            <span className="font-semibold text-slate-700">{entered} inserite</span>
            <span className="text-emerald-600 font-semibold" data-testid="mo-ready-count">{ready} pronte</span>
            {toFix > 0 && <span className="text-amber-600 font-semibold" data-testid="mo-tofix-count">{toFix} da correggere</span>}
            {createdCount > 0 && <span className="text-slate-500">{createdCount} già create</span>}
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#F1EEF9] text-slate-600">
                  <th className="px-2 py-2 text-left font-semibold w-10">#</th>
                  {columns.map((col) => (
                    <th key={col.key} className="px-2 py-2 text-left font-semibold min-w-[160px]">
                      {col.label}{col.required !== false && <span className="text-destructive"> *</span>}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-left font-semibold min-w-[110px]">Stato</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, r) => {
                  const locked = row.status === "created";
                  return (
                    <tr key={r} className={`border-t border-slate-100 ${locked ? "bg-emerald-50/40" : row.status === "error" ? "bg-amber-50/30" : ""}`} data-testid={`mo-row-${r}`}>
                      <td className="px-2 py-1 text-slate-400 text-xs text-center">{r + 1}</td>
                      {columns.map((col) => (
                        <td key={col.key} className="px-1 py-1 align-top min-w-[160px]">{renderCell(row, r, col)}</td>
                      ))}
                      <td className="px-2 py-1 align-middle">
                        {statusBadge(row)}
                        {row.error && row.status === "error" && <p className="text-[11px] text-slate-500 mt-0.5 max-w-[160px]">{row.error}</p>}
                      </td>
                      <td className="px-1 py-1 text-center align-middle">
                        <button data-testid={`mo-delete-row-${r}`} onClick={() => deleteRow(r)}
                          className="text-slate-400 hover:text-destructive transition-colors p-1" title="Elimina riga">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" data-testid="mo-add-row-btn" onClick={() => addRows(1)} className="rounded-lg">
              <Plus className="h-4 w-4 mr-1.5" /> Aggiungi riga
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Numero righe</span>
              <input type="number" min={1} max={500} value={addN} data-testid="mo-add-n-input"
                onChange={(e) => setAddN(e.target.value)}
                className="w-20 h-9 px-2 rounded-md border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              <Button variant="outline" size="sm" data-testid="mo-add-n-btn" onClick={() => addRows(addN)} className="rounded-lg">Aggiungi</Button>
            </div>
          </div>

          {result && (
            <div className="mt-5 rounded-xl border border-slate-200 bg-white shadow-sm p-4" data-testid="mo-result-banner">
              <p className="font-heading font-bold text-slate-900 mb-2">Inserimento completato</p>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold"><CheckCircle2 className="h-4 w-4" />{result.created} create</span>
                {result.error > 0 && <span className="inline-flex items-center gap-1.5 text-destructive font-semibold"><XCircle className="h-4 w-4" />{result.error} non create</span>}
              </div>
              {result.error > 0 && <p className="text-xs text-slate-500 mt-2">Le righe non create restano modificabili: correggile e premi di nuovo "Crea sedi". Le sedi già create non verranno ricreate.</p>}
            </div>
          )}

          <div className="mt-6 sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent pt-4 pb-2 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-slate-700" data-testid="mo-ready-label">{ready} {ready === 1 ? "sede pronta" : "sedi pronte"}</p>
            <Button data-testid="mo-submit-btn" onClick={submit} disabled={ready === 0 || submitting} className="rounded-lg font-semibold">
              <Send className="h-4 w-4 mr-2" />{submitting ? "Creazione in corso…" : `Crea ${ready} ${ready === 1 ? "sede" : "sedi"}`}
            </Button>
          </div>
        </>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="mo-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mo-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="mo-confirm-ok" onClick={() => { confirm?.apply?.(); setConfirm(null); }}>Procedi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
